import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { GridItem, SortType } from "../../models/tempur-love-grid";
import { fromSSRData } from "../../utils/ssr";
import { TempurLoveRootUrlContext } from "../../utils/tempurLoveGridContext";
import { Grid } from "../Grid";
import { SearchField } from "../SearchField";
import { SortDropdown } from "../SortDropdown";

interface GridApiResponse {
    results: GridItem[];
    next: string | null;
}

const validateGridResponse = (data: unknown): GridApiResponse => {
    const response = data as Record<string, unknown>;
    if (!Array.isArray(response.results)) {
        throw new Error("Invalid API response: results is not an array");
    }
    if (response.next !== null && typeof response.next !== "string") {
        throw new Error("Invalid API response: next is not a string or null");
    }
    return response as unknown as GridApiResponse;
};

const fetchGridData = async (
    baseApiUrl: string,
    sortType: SortType,
    filterBy?: number | null,
    searchString?: string,
    signal?: AbortSignal,
): Promise<GridApiResponse> => {
    const apiURL = new URL(baseApiUrl, getWindow()?.location.href);
    apiURL.searchParams.set("format", "json");
    apiURL.searchParams.set("ordering", sortType);

    if (filterBy) {
        apiURL.searchParams.set("category", `${filterBy}`);
    }
    if (searchString) {
        apiURL.searchParams.set("search", searchString);
    }

    const response = await fetch(apiURL.toString(), { signal });
    if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
    }
    return validateGridResponse(await response.json());
};

const fetchMoreItems = async (
    nextUrl: string,
    signal?: AbortSignal,
): Promise<GridApiResponse> => {
    const sameOriginUrl = new URL(
        nextUrl,
        getWindow()?.location.href,
    ).toString();
    const response = await fetch(sameOriginUrl, { signal });
    if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
    }
    return validateGridResponse(await response.json());
};

export const TempurLoveGrid = (
    props: Immutable<{
        filterBy?: number | null;
        baseApiUrl: string;
        initialGridData?: templates.TempurLoveGridTemplate["initial_grid_data"];
        initialNextUrl?: string | null;
        useCappedWidth?: boolean;
        rootUrl?: string | null;
    }>,
): React.ReactNode => {
    const useCappedWidth = props.useCappedWidth ?? true;
    const [gridData, setGridData] = React.useState<GridItem[]>(() => {
        if (!props.initialGridData) return [];
        return fromSSRData<GridItem[]>(props.initialGridData);
    });
    const [sortType, setSortType] = React.useState<SortType>("-date");
    const [nextUrl, setNextUrl] = React.useState<string | null>(
        props.initialNextUrl ?? null,
    );
    const [appendItems, setAppendItems] = React.useState(false);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const hasInitialData = React.useRef(
        !!props.initialGridData && props.initialGridData.length > 0,
    );
    const abortControllerRef = React.useRef<AbortController | null>(null);

    // Abort any in-flight request on unmount
    React.useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const updateGrid = React.useCallback(
        async (searchString?: string): Promise<void> => {
            // Cancel any pending loadMore or previous updateGrid fetch
            abortControllerRef.current?.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;

            setError(null);
            setIsLoadingMore(false);
            try {
                const data = await fetchGridData(
                    props.baseApiUrl,
                    sortType,
                    props.filterBy,
                    searchString,
                    controller.signal,
                );
                if (controller.signal.aborted) return;
                setGridData(data.results);
                setNextUrl(data.next);
                setAppendItems(false);
            } catch (err) {
                if (err instanceof DOMException && err.name === "AbortError") {
                    return;
                }
                console.error("Failed to update grid:", err);
                setError(t`Failed to load posts. Please try again.`);
            }
        },
        [props.baseApiUrl, props.filterBy, sortType],
    );
    const updateGridRef = React.useRef(updateGrid);

    React.useEffect(() => {
        updateGridRef.current = updateGrid;
    }, [updateGrid]);

    const loadMore = (): void => {
        if (!nextUrl || isLoadingMore) return;
        // Cancel any pending fetch before starting loadMore
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoadingMore(true);
        setError(null);
        fetchMoreItems(nextUrl, controller.signal)
            .then((data) => {
                if (controller.signal.aborted) return;
                setGridData((prevGridData) => [
                    ...prevGridData,
                    ...data.results,
                ]);
                setNextUrl(data.next);
                setAppendItems(true);
            })
            .catch((err) => {
                if (err instanceof DOMException && err.name === "AbortError") {
                    return;
                }
                console.error("Failed to load more items:", err);
                setError(t`Failed to load more posts. Please try again.`);
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoadingMore(false);
                }
            });
    };

    const handleSortChange = (
        e: React.ChangeEvent<HTMLSelectElement>,
    ): void => {
        setSortType(e.target.value as SortType);
    };

    React.useEffect(() => {
        if (hasInitialData.current) {
            hasInitialData.current = false;
            return;
        }
        updateGridRef.current().catch(() => {
            // Error state is set inside updateGrid
        });
    }, [sortType]);

    const handleSearch = (searchString?: string): void => {
        updateGrid(searchString).catch(() => {
            // Error state is set inside updateGrid
        });
    };

    // Search section always uses capped width regardless of useCappedWidth prop
    const searchSection = (
        <div className="l-full-width">
            <div className="l-capped-width">
                <article className="search">
                    <SearchField onSearch={handleSearch} />
                    <SortDropdown
                        onChange={handleSortChange}
                        value={sortType}
                    />
                </article>
            </div>
        </div>
    );

    return (
        <TempurLoveRootUrlContext.Provider value={props.rootUrl ?? null}>
            {searchSection}
            {error && (
                <p className="posts__error" role="alert">
                    {error}
                </p>
            )}
            {gridData.length > 0 && (
                <Grid
                    data={gridData}
                    appendItems={appendItems}
                    useCappedWidth={useCappedWidth}
                />
            )}
            {nextUrl && (
                <button
                    className="button button--full-width button--alabaster load-more-posts al-tempur-love--load-more-posts"
                    disabled={isLoadingMore}
                    onClick={loadMore}
                >
                    {isLoadingMore ? t`Loading...` : t`Load More Posts`}
                </button>
            )}
        </TempurLoveRootUrlContext.Provider>
    );
};
